<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div>
    <Navbar />
    <div class="moderation-page">
      <el-card shadow="always">
        <template #header>
          <div class="moderation-header">
            <h2>Modération</h2>
            <el-radio-group v-model="statusFilter" size="small" @change="fetchReports">
              <el-radio-button value="OPEN">À traiter</el-radio-button>
              <el-radio-button value="CLOSED">Traités</el-radio-button>
              <el-radio-button value="">Tous</el-radio-button>
            </el-radio-group>
          </div>
        </template>

        <el-alert
          v-if="forbidden"
          type="error"
          :closable="false"
          show-icon
          title="Cette page est réservée aux administrateurs et aux modérateurs."
        />
        <template v-else>
          <el-empty v-if="loaded && reports.length === 0" description="Aucun signalement." />
          <div v-for="report in reports" :key="report.id" class="report-item">
            <div class="report-head">
              <el-tag :type="targetTags[targetType(report)].type" size="small">
                {{ targetTags[targetType(report)].label }}
              </el-tag>
              <el-tag v-if="report.status === 'CLOSED'" type="info" size="small">Traité</el-tag>
              <span class="report-date">{{ formatDate(report.createdAt) }}</span>
            </div>

            <p class="report-reason">{{ report.reason }}</p>
            <p v-if="report.message" class="report-details">« {{ report.message }} »</p>
            <p class="report-meta">
              Signalé par <UserLink :username="report.reporter && report.reporter.username" />
            </p>

            <!-- Cible : commentaire -->
            <!-- Commentaire supprimé : reportedCommentId passe à NULL (ON DELETE SET NULL), affiché comme « Membre » -->
            <div v-if="targetType(report) === 'comment'" class="report-target">
              <p class="target-excerpt">{{ excerpt(report.reportedComment.text) }}</p>
              <p class="report-meta">
                Auteur : <UserLink :username="report.reportedComment.User && report.reportedComment.User.username" />
              </p>
            </div>
            <!-- Cible : publication -->
            <div v-else-if="targetType(report) === 'post'" class="report-target">
              <template v-if="report.reportedPost">
                <p class="target-excerpt">{{ excerpt(report.reportedPost.content) }}</p>
                <p class="report-meta">
                  Auteur : <UserLink :username="report.reportedPost.User && report.reportedPost.User.username" />
                </p>
              </template>
              <p v-else class="report-meta">Publication supprimée.</p>
            </div>
            <!-- Cible : membre -->
            <div v-else class="report-target">
              <p class="report-meta">
                Membre : <UserLink :username="report.reportedUser && report.reportedUser.username" />
              </p>
            </div>

            <div class="report-actions">
              <el-button
                v-if="report.reportedPost"
                size="small"
                @click="viewPost(report.reportedPost.id)"
              >
                Voir la publication
              </el-button>
              <el-button
                v-if="report.reportedPost && report.status === 'OPEN'"
                size="small"
                type="danger"
                @click="deletePost(report)"
              >
                Supprimer la publication
              </el-button>
              <template v-if="report.reportedComment">
                <el-button size="small" @click="viewPost(report.reportedComment.postId)">
                  Voir la publication
                </el-button>
                <el-button
                  v-if="report.status === 'OPEN'"
                  size="small"
                  type="danger"
                  class="delete-comment-btn"
                  @click="deleteComment(report)"
                >
                  Supprimer le commentaire
                </el-button>
              </template>
              <el-button
                v-if="report.status === 'OPEN'"
                size="small"
                type="success"
                @click="closeReport(report)"
              >
                Marquer comme traité
              </el-button>
            </div>
          </div>
        </template>
      </el-card>
    </div>
  </div>
</template>

<script>
import Navbar from '@/components/Navbar.vue'
import UserLink from '@/components/UserLink.vue'
import api from '@/api'
import { notifySuccess, notifyError, confirmAction } from '@/notify'

export default {
  components: { Navbar, UserLink },

  data() {
    return {
      reports: [],
      statusFilter: 'OPEN',
      targetTags: {
        comment: { label: 'Commentaire', type: 'warning' },
        post: { label: 'Publication', type: 'warning' },
        user: { label: 'Membre', type: 'danger' },
      },
      loaded: false,
      forbidden: false,
    }
  },

  async created() {
    await this.fetchReports()
  },

  mounted() {
    document.body.style.backgroundColor = 'rgba(21, 109, 123)'
  },

  methods: {
    async fetchReports() {
      try {
        const query = this.statusFilter ? `?status=${this.statusFilter}` : ''
        const response = await api.get(`/report/getAllReport${query}`)
        this.reports = (response.data && response.data.reports) || []
        this.forbidden = false
      } catch (error) {
        if (error.response && error.response.status === 403) {
          this.forbidden = true
        } else {
          notifyError(error, 'Impossible de charger les signalements')
        }
      } finally {
        this.loaded = true
      }
    },

    async closeReport(report) {
      try {
        await api.patch(`/report/${report.id}/close`)
        notifySuccess('Signalement traité')
        await this.fetchReports()
      } catch (error) {
        notifyError(error, 'Impossible de clore le signalement')
      }
    },

    // Supprime la publication signalée puis clôt le signalement
    async deletePost(report) {
      if (!(await confirmAction('Supprimer définitivement cette publication ?', { confirmText: 'Supprimer' }))) return
      try {
        await api.delete(`/post/delete/${report.reportedPost.id}`)
        await api.patch(`/report/${report.id}/close`)
        notifySuccess('Publication supprimée')
        await this.fetchReports()
      } catch (error) {
        notifyError(error, 'Erreur lors de la suppression')
      }
    },

    // Supprime le commentaire signalé (et ses réponses) puis clôt le signalement
    async deleteComment(report) {
      if (!(await confirmAction('Supprimer définitivement ce commentaire ?', { confirmText: 'Supprimer' }))) return
      const { id, postId } = report.reportedComment
      try {
        await api.delete(`/post/${postId}/removeComment/${id}`)
        await api.patch(`/report/${report.id}/close`)
        notifySuccess('Commentaire supprimé')
        await this.fetchReports()
      } catch (error) {
        notifyError(error, 'Erreur lors de la suppression')
      }
    },

    // Cible du signalement ; un commentaire porte aussi reportedUserId (son auteur), d'où l'ordre des tests
    targetType(report) {
      if (report.reportedComment) return 'comment'
      if (report.reportedPostId || report.reportedPost) return 'post'
      return 'user'
    },

    viewPost(postId) {
      this.$router.push({ name: 'Post', params: { postId } })
    },

    excerpt(text) {
      if (!text) return ''
      return text.length > 200 ? `${text.slice(0, 200)}…` : text
    },

    formatDate(dateString) {
      const options = { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric' }
      return new Date(dateString).toLocaleDateString('fr-FR', options)
    },
  },
}
</script>

<style scoped>
.moderation-page {
  padding: 70px 20px 20px;
  max-width: 800px;
  margin: 0 auto;
}

.moderation-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.moderation-header h2 {
  margin: 0;
  color: #303133;
}

.report-item {
  padding: 14px 0;
  border-bottom: 1px solid #ebeef5;
}

.report-head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.report-date {
  margin-left: auto;
  font-size: 12px;
  color: #909399;
}

.report-reason {
  margin: 10px 0 4px;
  color: #303133;
  font-weight: 600;
}

.report-details {
  margin: 0 0 4px;
  color: #606266;
  font-style: italic;
  white-space: pre-wrap;
}

.report-meta {
  margin: 4px 0;
  font-size: 13px;
  color: #606266;
}

.report-target {
  margin: 8px 0;
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 6px;
}

.target-excerpt {
  margin: 0 0 4px;
  color: #303133;
  white-space: pre-wrap;
}

.report-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}
</style>
